/**
 * BoundaryDrawingModal — full-screen modal for drawing a food-source boundary polygon.
 *
 * Opens as a portal over the full viewport (same pattern as MapPickerModal).
 * The map is immediately interactive — no tap-to-interact gate needed since the
 * user explicitly opened this modal to draw.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import maplibregl from 'maplibre-gl';

import { HuntMap } from '@/components/map/HuntMap';
import { useMap } from '@/hooks/useMap';
import { MapStyleSwitcher } from '@/components/map/MapStyleSwitcher';
import { TILE_LAYERS } from '@/services/map-tiles';
import type { TileLayerKey } from '@/services/map-tiles';
import { computeAcreage } from '@/utils/boundary-utils';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';

// ─── Drawing layer IDs ────────────────────────────────────────────────────────

const SOURCE_ID = 'bdm-source';
const FILL_LAYER_ID = 'bdm-fill';
const LINE_LAYER_ID = 'bdm-line';
const VERTEX_COLOR = '#22C55E';

// ─── Vertex marker element ────────────────────────────────────────────────────

function makeVertexEl(index: number): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = `
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${VERTEX_COLOR};
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    font-weight: 700;
    color: #fff;
    font-family: 'DM Sans', system-ui, sans-serif;
    cursor: grab;
  `;
  // SAFETY: index is a number, not user-controlled HTML
  el.textContent = String(index + 1);
  return el;
}

// ─── Drawing canvas hook (rendered inside HuntMap's MapProvider) ──────────────

interface DrawingCanvasProps {
  map: maplibregl.Map;
  vertices: [number, number][];
  onVertexMove: (idx: number, lat: number, lng: number) => void;
}

function useDrawingCanvas({ map, vertices, onVertexMove }: DrawingCanvasProps) {
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const mountedRef = useRef(true);

  function removeLayers() {
    try {
      if (map.getLayer(FILL_LAYER_ID)) map.removeLayer(FILL_LAYER_ID);
      if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID);
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    } catch { /* map may already be destroyed */ }
  }

  function removeMarkers() {
    for (const m of markersRef.current) m.remove();
    markersRef.current = [];
  }

  function rebuild() {
    if (!mountedRef.current) return;
    removeLayers();
    removeMarkers();

    if (vertices.length === 0) return;

    const coords = vertices.map(([lat, lng]) => [lng, lat]);

    if (vertices.length >= 2) {
      const isPolygon = vertices.length >= 3;
      const lineCoords = isPolygon ? [...coords, coords[0]] : coords;

      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: isPolygon
            ? { type: 'Polygon', coordinates: [lineCoords] }
            : { type: 'LineString', coordinates: lineCoords },
          properties: {},
        },
      });

      if (isPolygon) {
        map.addLayer({
          id: FILL_LAYER_ID,
          type: 'fill',
          source: SOURCE_ID,
          paint: { 'fill-color': VERTEX_COLOR, 'fill-opacity': 0.15 },
        });
      }

      map.addLayer({
        id: LINE_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        paint: {
          'line-color': VERTEX_COLOR,
          'line-width': 2,
          'line-opacity': 0.7,
          'line-dasharray': [6, 4],
        },
      });
    }

    for (let i = 0; i < vertices.length; i++) {
      const [lat, lng] = vertices[i];
      const idx = i;
      const el = makeVertexEl(idx);
      const marker = new maplibregl.Marker({ element: el, draggable: true, anchor: 'center' })
        .setLngLat([lng, lat])
        .addTo(map);
      marker.on('dragend', () => {
        const pos = marker.getLngLat();
        onVertexMove(idx, pos.lat, pos.lng);
      });
      markersRef.current.push(marker);
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    map.on('style.load', rebuild);
    if (map.isStyleLoaded()) rebuild();

    return () => {
      mountedRef.current = false;
      map.off('style.load', rebuild);
      removeLayers();
      removeMarkers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vertices, onVertexMove]);
}

interface DrawingCanvasControllerProps {
  vertices: [number, number][];
  onVertexMove: (idx: number, lat: number, lng: number) => void;
}

function DrawingCanvasController({ vertices, onVertexMove }: DrawingCanvasControllerProps) {
  const map = useMap();
  useDrawingCanvas({ map, vertices, onVertexMove });
  return null;
}

// ─── BoundaryDrawingModal ─────────────────────────────────────────────────────

export interface BoundaryDrawingModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the final vertex array when user taps Save. */
  onSave: (vertices: [number, number][]) => void;
  /** Map center as [lat, lng] — waypoint coords, or property coords as fallback. */
  center: [number, number];
  /** Pre-existing vertices when editing an existing boundary. */
  initialVertices?: [number, number][];
}

export function BoundaryDrawingModal({
  isOpen,
  onClose,
  onSave,
  center,
  initialVertices,
}: BoundaryDrawingModalProps) {
  const [vertices, setVertices] = useState<[number, number][]>(initialVertices ?? []);
  const [activeStyle, setActiveStyle] = useState<TileLayerKey>('satellite');
  const mapRef = useRef<maplibregl.Map | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Lock body scroll while open
  useBodyScrollLock(isOpen);

  // Sync tile style to live map
  useEffect(() => {
    if (mapRef.current && mountedRef.current) {
      try { mapRef.current.setStyle(TILE_LAYERS[activeStyle].style); } catch { /* map may be destroyed */ }
    }
  }, [activeStyle]);

  const handleVertexMove = useCallback((idx: number, lat: number, lng: number) => {
    setVertices((prev) => {
      const next = [...prev] as [number, number][];
      next[idx] = [lat, lng];
      return next;
    });
  }, []);

  function handleMapReady(map: maplibregl.Map) {
    mapRef.current = map;
    map.getCanvas().style.cursor = 'crosshair';
    map.on('click', (e) => {
      setVertices((prev) => [...prev, [e.lngLat.lat, e.lngLat.lng]]);
    });
  }

  function handleUndo() {
    setVertices((prev) => prev.slice(0, -1));
  }

  function handleClear() {
    setVertices([]);
  }

  function handleSave() {
    onSave(vertices);
  }

  const acreage = vertices.length >= 3 ? computeAcreage(vertices) : null;
  const canSave = vertices.length >= 3;

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col bg-bark-950"
      role="dialog"
      aria-modal="true"
      aria-label="Draw boundary"
      data-testid="boundary-drawing-modal"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 bg-bark-900 border-b border-bark-700 shrink-0 h-14">
        <button
          type="button"
          onClick={onClose}
          className="text-body-sm text-ember-400 hover:text-ember-300 transition-colors"
          aria-label="Cancel"
        >
          ← Cancel
        </button>
        <div className="flex flex-col items-center">
          <span className="text-body-sm font-semibold text-bark-100">Draw Boundary</span>
          <span className="text-body-xs text-bark-400" aria-live="polite">
            {vertices.length === 0
              ? 'Tap map to place points'
              : vertices.length < 3
              ? `${vertices.length} point${vertices.length === 1 ? '' : 's'} — need 3 to close`
              : `${vertices.length} points · ~${acreage!.toFixed(1)} acres`}
          </span>
        </div>
        {/* Spacer to balance cancel button */}
        <div className="w-16" aria-hidden="true" />
      </div>

      {/* Map area */}
      <div className="relative flex-1 min-h-0">
        <HuntMap
          center={center}
          zoom={15}
          className="h-full w-full"
          initialStyle="satellite"
          onMapReady={handleMapReady}
        >
          <DrawingCanvasController vertices={vertices} onVertexMove={handleVertexMove} />
        </HuntMap>

        {/* Style switcher */}
        <div style={{ zIndex: 1000 }} className="absolute bottom-4 right-3">
          <MapStyleSwitcher active={activeStyle} onChange={setActiveStyle} />
        </div>
      </div>

      {/* Bottom action strip */}
      <div className="shrink-0 px-4 pt-3 pb-4 bg-bark-900 border-t border-bark-700 flex items-center gap-2">
        <button
          type="button"
          onClick={handleUndo}
          disabled={vertices.length === 0}
          className="px-3 py-2 rounded-md text-body-sm text-bark-200 bg-bark-800 border border-bark-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-bark-700 transition-colors"
        >
          Undo
        </button>
        <button
          type="button"
          onClick={handleClear}
          disabled={vertices.length === 0}
          className="px-3 py-2 rounded-md text-body-sm text-bark-200 bg-bark-800 border border-bark-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-bark-700 transition-colors"
        >
          Clear
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          data-testid="boundary-drawing-modal-save"
          className="px-4 py-2 rounded-md text-body-sm font-semibold bg-ember-500 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-ember-400 transition-colors"
        >
          Save Boundary
        </button>
      </div>
    </div>,
    document.body,
  );
}
